// react components
import { useState, useEffect } from "react";

//api
import api from "api/api";

// mui components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";
//components
import Item from "./layout/Item";
import GraphLayout from "./layout/GraphLayout";
import AdditionalProductPerformanceLayout from "./layout/AdditionalProductPerformanceLayout";
import LineGraphs from "./charts/LineGraphs";
import PieCharts from "./charts/PieCharts";
import HorizontalBarGraphs from "./charts/HorizontalBarGraphs";
import HalfDoughnutCharts from "./charts/HalfDoughnutCharts";
import DoughnutCharts from "./charts/DoughnutCharts";
import StatsTable from "./layout/StatsTable";

//redux
import { useSelector, useDispatch } from "react-redux";
import { loadSwitchState } from "store/features/respondentDashboardSlice";
import { showNotification } from "store/features/notificationSlice";
//import assets
import interviewRespondents from "../../assets/Interview-respondants.png";
import PersonFill from "./charts/PersonFill";
import VerticalBarGraph from "./charts/VerticalBarGraph";
import { consumerFilterPayload } from "../../utils/consumerFilterUtils";

function Dashboard({ data = [], overallData = {}, additionalProductPerformanceData = [], filterData = {}, settings = [], fetchSettingsData = () => {}, navigateFrom = "", textThemeFilters = {} }) {
    const dispatch = useDispatch();

    const switchState = useSelector((state) => state.respondentDashboardStore.switchState);
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productsTestCodeMapData = useSelector((state) => state.studyDataStore.productTestCodeMap);
    const selectedStudy = useSelector((state) => state.studyDataStore.selectedStudies);
    const consumerFilterStoreData = useSelector((state) => state.consumerFilterStore.currentSelectedFilters);
    const consumerFilterStoreDataForCorrelation = useSelector((state) => state.correlationStore.currentSelectedFiltersForCorrelation);

    const isOralCare = process.env?.REACT_APP_ORAL_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();

    const [screenerData, setScreenerData] = useState([]);
    const [recallData, setRecallData] = useState([]);

    const [countryData, setCountryData] = useState([]);

    // drag start var
    const [dragStartVarType, setDragStartVarType] = useState(null);

    const [showAstrik, setShowAstrik] = useState(false);

    useEffect(() => {
        if (data.length > 0) {
            const tempScreener = data.filter((item) => item.type_of_variable.toLowerCase() === "screener").sort((a, b) => parseInt(a.sequence_number) - parseInt(b.sequence_number));
            setScreenerData([...tempScreener]);
            const tempRecall = data.filter((item) => item.type_of_variable.toLowerCase() === "recall").sort((a, b) => parseInt(a.sequence_number) - parseInt(b.sequence_number));
            setRecallData([...tempRecall]);
        }

        if (Object.keys(overallData).length > 0 && overallData.country_sample) {
            let tempCountry = [];
            Object.keys(overallData?.country_sample).forEach((datum) => {
                const countryInfo = overallData.country_sample[datum];
                const countryObj = {
                    percentage: countryInfo.percentage,
                    sample_size: countryInfo.count,
                    title: datum,
                };
                if (countryObj.sample_size > 0) {
                    tempCountry.push(countryObj);
                }
            });
            setCountryData(tempCountry);
        }
    }, [data, overallData]);

    const fetchSwitchState = (key, value) => {
        const datum = { ...switchState };
        datum[key] = value;
        dispatch(loadSwitchState({ ...datum }));
    };

    const showGraphByType = (type, statsData, graphData) => {
        if (type) {
            switch (type.toLowerCase()) {
                // case "bar":
                //     return <BarGraphs data={statsData} />;
                case "line":
                    return <LineGraphs data={statsData} />;
                case "pie":
                    return <PieCharts data={statsData} />;
                case "horizontal_bar":
                    return <HorizontalBarGraphs data={statsData} />;
                case "vertical_bar":
                    return <VerticalBarGraph data={statsData} />;
                case "full_donut":
                    return <DoughnutCharts data={statsData} />;
                case "half_donut":
                    return <HalfDoughnutCharts data={statsData} />;
                case "default":
                    return (
                        <PersonFill
                            graphData={graphData}
                            statsData={statsData}
                        />
                    );
                default:
                    return null; // You can modify this to return a default component
            }
        }
    };

    const handleDragStart = (event, id, type) => {
        event.dataTransfer.setData("text/plain", id);
        setDragStartVarType(type);
    };

    const handleDrop = (event, dropId, type) => {
        if (type === dragStartVarType) {
            const draggedId = parseInt(event.dataTransfer.getData("text/plain"), 10);
            if (draggedId !== dropId && draggedId && dropId) {
                if (type === "screener") {
                    const draggedItemIndex = screenerData.findIndex((item) => parseInt(item.sequence_number) === draggedId);
                    const dropItemIndex = screenerData.findIndex((item) => parseInt(item.sequence_number) === dropId);
                    updateSequence(screenerData[dropItemIndex], screenerData[draggedItemIndex]);

                    const updatedItems = [...screenerData];
                    updatedItems[draggedItemIndex].sequence_number = dropId;
                    updatedItems[dropItemIndex].sequence_number = draggedId;
                    updatedItems.sort((a, b) => parseInt(a.sequence_number) - parseInt(b.sequence_number));

                    setScreenerData(updatedItems);
                } else if (type === "recall") {
                    const draggedItemIndex = recallData.findIndex((item) => parseInt(item.sequence_number) === draggedId);
                    const dropItemIndex = recallData.findIndex((item) => parseInt(item.sequence_number) === dropId);
                    updateSequence(recallData[dropItemIndex], recallData[draggedItemIndex]);

                    const updatedItems = [...recallData];
                    updatedItems[draggedItemIndex].sequence_number = dropId;
                    updatedItems[dropItemIndex].sequence_number = draggedId;
                    updatedItems.sort((a, b) => parseInt(a.sequence_number) - parseInt(b.sequence_number));

                    setRecallData(updatedItems);
                }
            }
        } else {
            handleError("error", "Rearrangement is only allowed within the same variable type.");
        }

        event.preventDefault();
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const updateSequence = async (drop, drag) => {
        if (settings.length > 0) {
            const dragItem = settings.filter((ele) => ele.backend_variable === drag.backend_variable);
            const draggedId = dragItem[0].id;
            const dropItem = settings.filter((ele) => ele.backend_variable === drop.backend_variable);
            const droppedId = dropItem[0].id;

            const url = process.env.REACT_APP_SERVER_PATH + `/respondent-settings/sequence-number/`;
            const payload = {
                [draggedId]: drop.sequence_number,
                [droppedId]: drag.sequence_number,
            };

            await api
                .post(url, payload)
                .then(() => {
                    setDragStartVarType(null);
                    fetchSettingsData();
                })
                .catch((error) => {
                    handleError("error", error);
                });
        }
    };

    const handleError = (type, error) => {
        dispatch(showNotification({ severity: type, data: error }));
    };

    const getProductNameWithTestcodes = (productName, studyReference) => {
        const coredataId = selectedStudy.find((study) => study.study_reference__title === studyReference)?.id;
        const proTitle = `${productName}__${coredataId}`;
        const proTestCode = productsTestCodeMapData[proTitle];
        return proTestCode;
    };

    const isSequentialMonadicSelected = (studyReference) => {
        const matchingStudy = selectedStudy.find((study) => study.study_reference__title === studyReference);
        const isSeqMonadicProduct = matchingStudy?.test_design?.includes("Seq. Monadic");
        if (isSeqMonadicProduct && !showAstrik) {
            setShowAstrik(true);
        }
        return isSeqMonadicProduct;
    };
    return (
        <>
            <Box
                sx={{
                    flexGrow: 1,
                    p: 1,
                    mb: 1,
                }}
            >
                <Grid
                    container
                    spacing={{ xs: 2, md: 3 }}
                    columns={{ xs: 4, sm: 8, md: 12, lg: 12 }}
                    sx={{
                        m: 1,
                    }}
                >
                    <Grid
                        item
                        xs={4}
                        sm={4}
                        md={4}
                        lg={4}
                    >
                        <div
                            className=" text-center p-3 rounded box-shadow--2"
                            style={{ height: "100%", backgroundColor: "#FFFFFF" }}
                        >
                            <h5 className="text-base color-blue">Overall Respondents Count</h5>
                            <img
                                style={{ width: "300px" }}
                                src={interviewRespondents}
                                alt="interview-respondents"
                            />
                            <p style={{ fontSize: "30px", fontWeight: "bold", color: "#00d3ff" }}>{Object.keys(overallData).length > 0 && overallData?.overall_sample}</p>
                        </div>
                    </Grid>
                    <Grid
                        item
                        xs={4}
                        sm={4}
                        md={4}
                        lg={4}
                    >
                        <div
                            className="text-center p-3 rounded box-shadow--2"
                            style={{ height: "100%", backgroundColor: "#FFFFFF" }}
                        >
                            <h5 className="text-base color-blue">Product Level Respondents Counts</h5>
                            <div
                                style={{
                                    marginTop: "20px",
                                    height: "300px",
                                    overflowY: "scroll",
                                }}
                            >
                                {Object.keys(overallData).length > 0 &&
                                    Object.keys(overallData?.product_sample).map((datum, i) => {
                                        const splitArray = datum.split("/").map((word) => word.trim());
                                        let productTitle;
                                        let productName;
                                        let studyReference;
                                        if (splitArray.length === 2) {
                                            productTitle = splitArray[0];
                                            productName = splitArray[0];
                                            studyReference = splitArray[1];
                                        } else if (splitArray.length === 3) {
                                            productTitle = splitArray[0] + " / " + splitArray[1];
                                            productName = splitArray[0];
                                            studyReference = splitArray[2];
                                        } else if (splitArray.length >= 4) {
                                            // Handle cases like "St. Ives Shea Butter/Oatmeal / 596/test-reference1510" (Skin Care)
                                            // Combine everything except the last 2 parts using negative index
                                            const productParts = splitArray.slice(0, -2);
                                            productTitle = productParts.join(" / ");
                                            productName = splitArray[0];
                                            studyReference = splitArray[splitArray.length - 2];
                                        } else {
                                            // Fallback for length 1 or 0
                                            productTitle = datum;
                                            productName = datum;
                                            studyReference = "Unknown";
                                        }
                                        const coreDataId = selectedStudy.find((study) => study.study_reference__title === studyReference)?.id;
                                        const productCode = productTitle.split(" ").filter(item => item != "").join("_");

                                        // Check if we received Text Theme (TTA) filters from the previous page
                                        const hasTTAFilters = textThemeFilters && Object.keys(textThemeFilters).length > 0;
                                        let hasFilter;
                                        
                                        if (hasTTAFilters) {
                                            // We came from the Text Theme page, so check the TTA filters for this product
                                            const ttaKey = `${productCode}__${coreDataId}`;
                                            const ttaFilterObj = textThemeFilters[ttaKey] || {};
                                            hasFilter = Object.keys(ttaFilterObj).length > 0;
                                        } else {
                                            // We came from a normal page, so check the global filter store
                                            const cfData = navigateFrom === "/correlation" ? consumerFilterStoreDataForCorrelation : consumerFilterStoreData;
                                            const filterObject = consumerFilterPayload(cfData, productCode, coreDataId);
                                            hasFilter = filterObject && Object.keys(filterObject).length > 0;
                                        }
                                        const testCode = getProductNameWithTestcodes(productTitle, studyReference);
                                        const respondentCount = overallData["product_sample"][datum];
                                        const asterisk = isSequentialMonadicSelected(studyReference) ? "*" : "";
                                        const key = i + 1;
                                        return (
                                            <div
                                                key={key}
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <span
                                                    className="text-sm"
                                                    style={{
                                                        fontWeight: "bold",
                                                        color: "#00d3ff",
                                                        width: "15%",
                                                        marginRight: "10px",
                                                    }}
                                                >
                                                    {respondentCount} {asterisk && <span style={{ color: "#000" }}>{asterisk}</span>}
                                                </span>
                                                <span
                                                    style={{
                                                        width: "80%",
                                                        marginRight: "10px",
                                                        marginBottom: "5px",
                                                        textAlign: "left",
                                                        padding: "0px 0px 0px 5px",
                                                    }}
                                                    className="text-sm"
                                                >
                                                    {isOralCare ? (
                                                        <span>
                                                            {productName} / <span style={{ color: "#6f42c1" }}>{testCode}</span> / {studyReference}
                                                            {hasFilter ? <span style={{ fontWeight: "bold", fontSize: "14px" }}>*</span> : null}
                                                        </span>
                                                    ) : (
                                                        <span>
                                                            {datum}
                                                            {hasFilter ? <span style={{ fontWeight: "bold", fontSize: "14px" }}>*</span> : null}
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                        );
                                    })}
                            </div>
                            {showAstrik && (
                                <div
                                    style={{
                                        marginTop: "15px",
                                        display: "flex",
                                        alignItems: "flex-start",
                                        maxWidth: "600px",
                                    }}
                                >
                                    <span
                                        style={{
                                            color: "#000",
                                            fontWeight: "bold",
                                            fontSize: "13px",
                                            marginRight: "6px",
                                            lineHeight: "1.4",
                                        }}
                                    >
                                        *
                                    </span>
                                    <p
                                        style={{
                                            fontSize: "11px",
                                            color: "#555",
                                            textAlign: "left",
                                            lineHeight: "1.5",
                                            margin: 0,
                                        }}
                                    >
                                        In Sequential Monadic Assessment, the same respondent evaluates multiple products. Therefore, the ‘Product Level Respondents Counts’ equals the overall study sample size.
                                    </p>
                                </div>
                            )}
                        </div>
                    </Grid>
                    <Grid
                        item
                        xs={4}
                        sm={4}
                        md={4}
                        lg={4}
                    >
                        <div
                            className="text-center p-3 rounded box-shadow--2"
                            style={{ height: "100%", backgroundColor: "#FFFFFF" }}
                        >
                            <h5 className="text-base color-blue">Country</h5>
                            {countryData && countryData.length !== 0 && <DoughnutCharts data={countryData} />}
                        </div>
                    </Grid>
                </Grid>
            </Box>
            {screenerData.length > 0 ? (
                <div
                    className="full-container"
                    style={{
                        padding: "10px",
                        margin: "10px 10px 20px 10px",
                        border: "1px solid #005EEF",
                        boxShadow: "0px 3px 4px 0px rgba(17, 17, 26, 0.30)",
                    }}
                >
                    <h5 className="text-base color-blue">Screener</h5>
                    <Grid
                        container
                        spacing={{ xs: 2, md: 3 }}
                        columns={{ xs: 4, sm: 8, md: 12, lg: 12 }}
                        sx={{
                            m: 1,
                        }}
                    >
                        {screenerData &&
                            screenerData?.map((datum, index) => {
                                const key = index + 1;
                                return (
                                    <Grid
                                        item
                                        xs={12}
                                        sm={12}
                                        md={12}
                                        lg={6}
                                        key={key}
                                        onDragOver={handleDragOver}
                                        onDrop={(event) => handleDrop(event, parseInt(datum.sequence_number), "screener")}
                                        draggable
                                        onDragStart={(event) => handleDragStart(event, parseInt(datum.sequence_number), "screener")}
                                    >
                                        <Item>
                                            <GraphLayout
                                                title={datum?.display_label}
                                                backendVariable={datum?.backend_variable}
                                                fetchSwitchState={fetchSwitchState}
                                                sampleSize={datum?.sample_size}
                                                // fetchModalState={fetchModalState}
                                            >
                                                {datum?.sample_size === 0 && (
                                                    <span
                                                        style={{
                                                            display: "flex",
                                                            justifyContent: "center",
                                                            alignItems: "center",
                                                            height: "276px",
                                                        }}
                                                    >
                                                        No Data Available
                                                    </span>
                                                )}
                                                {datum?.sample_size > 0 && switchState[datum?.backend_variable] === "statsTable" && (
                                                    <StatsTable
                                                        data={datum?.stats}
                                                        title={datum?.display_label}
                                                    />
                                                )}
                                                {datum?.sample_size > 0 && switchState[datum?.backend_variable] === "countGraph" && showGraphByType(datum?.type_of_graph, datum?.stats, datum?.graph_data)}
                                            </GraphLayout>
                                        </Item>
                                    </Grid>
                                );
                            })}
                    </Grid>
                </div>
            ) : (
                ""
            )}
            {recallData.length > 0 ? (
                <div
                    className="full-container"
                    style={{
                        padding: "10px",
                        margin: "10px 10px 20px 10px",
                        border: "1px solid #005EEF",
                        boxShadow: "0px 3px 4px 0px rgba(17, 17, 26, 0.30)",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "flex-start",
                            marginBottom: "10px",
                        }}
                    >
                        <h5 className="text-base color-blue">Recall</h5>
                    </div>
                    <Grid
                        container
                        spacing={{ xs: 2, md: 3 }}
                        columns={{ xs: 4, sm: 8, md: 12, lg: 12 }}
                        sx={{
                            m: 1,
                        }}
                    >
                        {recallData &&
                            recallData?.map((datum, index) => {
                                const key = index + 1;
                                return (
                                    <Grid
                                        item
                                        xs={12}
                                        sm={12}
                                        md={12}
                                        lg={6}
                                        key={key}
                                        onDragOver={handleDragOver}
                                        onDrop={(event) => handleDrop(event, parseInt(datum.sequence_number), "recall")}
                                        draggable
                                        onDragStart={(event) => handleDragStart(event, parseInt(datum.sequence_number), "recall")}
                                    >
                                        <Item>
                                            <GraphLayout
                                                title={datum?.display_label}
                                                fetchSwitchState={fetchSwitchState}
                                                backendVariable={datum?.backend_variable}
                                                sampleSize={datum?.sample_size}
                                                // fetchModalState={fetchModalState}
                                            >
                                                {datum?.sample_size === 0 && (
                                                    <span
                                                        style={{
                                                            display: "flex",
                                                            justifyContent: "center",
                                                            alignItems: "center",
                                                            height: "276px",
                                                        }}
                                                    >
                                                        No Data Available
                                                    </span>
                                                )}
                                                {datum?.sample_size > 0 && switchState[datum?.backend_variable] === "statsTable" && (
                                                    <StatsTable
                                                        data={datum?.stats}
                                                        title={datum?.display_label}
                                                    />
                                                )}
                                                {datum?.sample_size > 0 && switchState[datum?.backend_variable] === "countGraph" && showGraphByType(datum?.type_of_graph, datum?.stats, datum?.graph_data)}
                                            </GraphLayout>
                                        </Item>
                                    </Grid>
                                );
                            })}
                    </Grid>
                </div>
            ) : (
                ""
            )}
            {additionalProductPerformanceData.length > 0 ? (
                <div
                    className="full-container"
                    style={{
                        padding: "10px",
                        margin: "10px 10px 20px 10px",
                        border: "1px solid #005EEF",
                        boxShadow: "0px 3px 4px 0px rgba(17, 17, 26, 0.30)",
                    }}
                >
                    <h5 className="text-base color-blue">Additional Product Performance</h5>
                    <Grid
                        container
                        spacing={{ xs: 2, md: 3 }}
                        columns={{ xs: 4, sm: 8, md: 12, lg: 12 }}
                        sx={{
                            m: 1,
                        }}
                    >
                        <Grid
                            item
                            xs={12}
                            sm={12}
                            md={12}
                            lg={12}
                        >
                            <Item>
                                {additionalProductPerformanceData.length > 0 ? (
                                    <AdditionalProductPerformanceLayout
                                        data={additionalProductPerformanceData}
                                        filterData={filterData}
                                    />
                                ) : (
                                    <div>No Data to show</div>
                                )}
                            </Item>
                        </Grid>
                    </Grid>
                </div>
            ) : (
                ""
            )}
        </>
    );
}

Dashboard.propTypes = {
    data: PropTypes.array,
    settings: PropTypes.array,
    overallData: PropTypes.object,
    additionalProductPerformanceData: PropTypes.array,
    filterData: PropTypes.object,
    fetchSettingsData: PropTypes.func,
    navigateFrom: PropTypes.string,
    textThemeFilters: PropTypes.object,
};

export default Dashboard;
